import { action } from 'easy-peasy';

import type { GenericObjectType } from '@/types';
import {
  getGlobals,
  getLocale,
  getUnifiedLoginContextData,
} from '@/lib/globals';
import { logger } from '@/lib/logger';
import type { CheckoutSession, Merchant } from '@/generated/graphql';
import { CheckoutSessionType } from '@/generated/graphql';
import type {
  ApiModel,
  ApiState,
  CartActions,
  CartState,
  GriffinActions,
  GriffinState,
  MiscApiActions,
  MiscApiState,
} from './types';

/**
 * CART MODEL
 * Data in this model is only set only once, when the
 * initial session data API call returns
 */
const getCartState = (): CartState => {
  const { checkoutSession } = getGlobals().initialGqlData;

  const { cart, merchant, creditMeta, checkoutSessionType } =
    checkoutSession || ({} as Partial<CheckoutSession>);

  const {
    country: merchantCountry,
    name: merchantName,
    billPayIntegratorName,
  } = merchant || ({} as Partial<Merchant>);

  const { currencyCode, currencyValue = 0 } = cart?.amounts?.total || {};
  const isBillingWithoutPurchase =
    checkoutSessionType === CheckoutSessionType.BillingWithoutPurchase;

  return {
    allowedCardTypes: [],
    cancelUrl: '',
    cartAmount: {
      currencyCode: currencyCode || '',
      currencyFormat: '',
      currencyFormatSymbolISOCurrency: '',
      currencySymbol: '',
      currencyValue: currencyValue || '',
    },
    cartDetails: {
      amounts: {},
      description: '',
      intent: '',
      items: [],
      merchant: {
        billPayIntegratorName: billPayIntegratorName || '',
        // @ts-expect-error - Should likely be null like graph would return
        country: merchantCountry || '',
        name: merchantName || '',
      },
      noteToBuyer: '',
    },
    consentCode: cart?.consentCode || '',
    creditMeta: {
      upstreamCreditPresentment: creditMeta?.upstreamCreditPresentment || false,
    },
    isBillingWithoutPurchase: isBillingWithoutPurchase,
    isShipToStore: false,
    lastCallbackShippingAddress: null,
    shippingMethods: [],
    subscriptionDetails: {
      initialPaymentInfo: cart?.subscriptionDetails?.initialPaymentInfo,
      plan: cart?.subscriptionDetails?.plan,
    },
  };
};

const createCartActions = (): CartActions => ({
  setCartData: action((state, payload) => {
    Object.assign(state, payload);
  }),
  setUpstreamCreditPresentment: action((state, payload) => {
    state.creditMeta.upstreamCreditPresentment = payload;
  }),
});

/**
 * GRIFFIN METADATA MODEL
 * This model contains metadata generated by Griffin that
 * is fetched from the API. It is set after the initial Griffin
 * metadata API call returns as well as when
 */
const getGriffinState = (): GriffinState => {
  const INITIAL_LOCALE = getLocale().locale;
  const { localeMetadata } = getGlobals().initialGqlData;

  return {
    griffinMetadata: {
      address: {},
      currencyCode: localeMetadata?.currencyCode,
      dateMeta: {
        datePattern: '',
        displayFormat: '',
      },
      phoneMeta: {
        mask: '',
        pattern: '',
      },
      territories: {
        [INITIAL_LOCALE]: [],
      },
      territoriesByRegion: {
        [INITIAL_LOCALE]: {
          AMERICAS: [],
          APAC: [],
          EMEA: [],
        },
      },
      territoriesToDialingCodeMap: {},
    },
  };
};

const createGriffinActions = (): GriffinActions => ({
  // eslint-disable-next-line sort-keys
  addAddressMetadata: action((state, { key, payload }) => {
    if (!state) {
      logger.warn('easy_peasy_state_missing', {
        action: 'addAddressMetadata',
      });
    }
    // `key` will be in the format of `country~lang`, e.g. 'US~en'
    state.griffinMetadata.address[key] = payload;
  }),
  setGriffinMetadata: action((state, payload) => {
    if (!state) {
      logger.warn('easy_peasy_state_missing', {
        action: 'setGriffinMetadata',
      });
    }
    /**
     * Spreading the payload for the following use case:
     * User with locale en_US lands on page and changes their country to GB
     * Since we don't overwrite the address meta here if the users changes
     * their shipping address to the US we won't have to re-fetch that address
     * meta for the US.
     *
     * NOTE: After calling this action to update `territories`, you should also rebuild the
     * CRS tax residency country list if the feature is enabled.
     * See buildCrsCountryList in components/App/utils.
     * This is already handled in:
     * - api.js (initial page load)
     * - fetchContent.ts (language change via buildCrsListIfReady)
     */
    state.griffinMetadata = {
      ...payload,
      address: {
        ...state.griffinMetadata.address,
        ...payload.address,
      },
      territories: {
        ...state.griffinMetadata.territories,
        ...payload.territories,
      },
      territoriesByRegion: {
        ...state.griffinMetadata.territoriesByRegion,
        ...payload.territoriesByRegion,
      },
    };
  }),
  updatePhoneMetadata: action((state, payload) => {
    if (!state) {
      logger.warn('easy_peasy_state_missing', {
        action: 'updatePhoneMetadata',
      });
    }
    state.griffinMetadata.phoneMeta = payload;
  }),
});

/**
 * MISC API MODEL
 * This model contains misc data returned from an API call
 */
const getMiscApiState = (): MiscApiState => {
  // Whether UL detected an existing account
  const { hasExistingAccount } = getUnifiedLoginContextData();

  const userAgreement = getGlobals()?.initialGqlData?.userAgreement;

  logger.info(
    `is_user_agreement_url_from_notice_and_consent_${Boolean(
      userAgreement?.activeUrl,
    )}`,
  );

  // Whether a merchant passed email is detected as an existing account
  const isUserAccount = Boolean(
    getGlobals().initialGqlData?.checkoutSession?.cart?.payer?.evaluatedUserData
      ?.emailHasExistingAccount,
  );

  const isExistingAccount =
    (typeof hasExistingAccount === 'boolean' && hasExistingAccount) ||
    isUserAccount;

  return {
    //Data pertaining to Bank Candidate data for Sepa flow
    bankCandidateData: {
      accountNumber: '',
      accountNumberShort: '',
      bankIdentifierCode: '',
      beneficiaries: [
        {
          fullName: '',
        },
      ],
      billingAddress: {
        city: '',
        fullAddress: '',
      },
      creditor: {
        address: {
          fullAddress: '',
        },
        id: '',
        name: '',
      },
      currencyCode: '',
      id: '',
      issuer: {
        branchLocation: '',
        city: '',
        // TODO: Can we default to a country code? Check code usage
        // @ts-expect-error - We default to a blank string
        country: '',
        name: '',
      },
      nickname: '',
      routingNumber: '',
      // TODO: Can we default to unknown? Check code usage
      // @ts-expect-error - We default to a blank string
      type: '',
    },

    bankCurrencyConversionData: {},

    // CRS tax residency country lists by locale
    crsTaxResidencyCountries: {
      territories: {},
    },

    /**
     * Default values for OTP
     * These are required by xobuyernodeserv
     */
    cupOtpEncryptedCardLoginAccountNumber: '',
    cupOtpfundingInstrumentId: '',

    currencyConversionData: {},

    installmentData: [],

    instrumentId: '',

    isExistingAccount,

    lastEmailLookup: null,

    merchantId: '',

    // Data pertaining to Normalized Billing Address
    // eslint-disable-next-line sort-keys
    normalizedBillingAddress: {
      alreadyNormalized: false,
      city: '',
      griffinFormattedAddresses: {
        formattedNormalizedAddress: [],
        formattedOriginalAddress: [],
      },
      line1: '',
      line2: '',
      postalCode: '',
      state: '',
    },

    // Data pertaining to Normalized Shipping Address
    // eslint-disable-next-line sort-keys
    normalizedShippingAddress: {
      alreadyNormalized: false,
      city: '',
      griffinFormattedAddresses: {
        formattedNormalizedAddress: [],
        formattedOriginalAddress: [],
      },
      line1: '',
      line2: '',
      postalCode: '',
      state: '',
    },

    // Data used to help determine what address state should be
    // validated on form submission
    // eslint-disable-next-line sort-keys
    prefillAddressMetadata: {
      hasPrefillAddress: false,
      isPartialAddress: false,
    },

    // Optional boolean used to help determine shipping calculations
    // during WPS Shipping and Tax flows
    profileOverrideAllowed: false,

    shippingOptionsSource: '',

    // Data pertaining to 3DS (1.0)
    threeDS1ContingencyData: {},

    // Data pertaining to 3DS 2.0
    threeDS2ContingencyData: {},

    userAgreement: {
      activeUrl: userAgreement?.activeUrl || '',
      majorVersion: userAgreement?.majorVersion || '',
      minorVersion: userAgreement?.minorVersion || '',
    },
  };
};

const createMiscApiActions = (): MiscApiActions => ({
  setBankCandidateData: action((state, payload) => {
    state.bankCandidateData = payload;
  }),

  setBankCurrencyConversionData: action((state, payload) => {
    state.bankCurrencyConversionData = payload;
  }),

  setCrsTaxResidencyCountries: action((state, payload) => {
    state.crsTaxResidencyCountries.territories = {
      ...state.crsTaxResidencyCountries.territories,
      ...payload.territories,
    };
  }),

  setCupOtpEncryptedCardLoginAccountNumber: action((state, payload) => {
    state.cupOtpEncryptedCardLoginAccountNumber = payload;
  }),

  setCupOtpFundingInstrumentId: action((state, payload) => {
    state.cupOtpfundingInstrumentId = payload;
  }),

  setCurrencyConversionData: action((state, payload) => {
    state.currencyConversionData = payload;
  }),

  setFundingSource: action((state, payload) => {
    state.fundingSource = payload;
  }),

  setInstallmentData: action((state, payload) => {
    state.installmentData = payload;
  }),

  setInstrumentId: action((state, payload) => {
    state.instrumentId = payload;
  }),

  setIsExistingAccount: action((state, payload) => {
    state.isExistingAccount = payload;
  }),

  setLastEmailLookup: action((state, payload) => {
    state.lastEmailLookup = payload;
  }),

  setMerchantId: action((state, payload) => {
    state.merchantId = payload;
  }),

  setNormalizedBillingAddress: action((state, payload) => {
    state.normalizedBillingAddress = payload;
  }),

  setNormalizedShippingAddress: action((state, payload) => {
    state.normalizedShippingAddress = payload;
  }),

  setPrefillAddressMetadata: action((state, payload) => {
    state.prefillAddressMetadata = {
      ...state.prefillAddressMetadata,
      ...payload,
    };
  }),

  setProfileOverrideAllowed: action((state, payload) => {
    state.profileOverrideAllowed = payload;
  }),

  setShippingOptionsSource: action((state, payload) => {
    state.shippingOptionsSource = payload;
  }),

  setThreeDS1ContingencyData: action((state, payload) => {
    state.threeDS1ContingencyData = payload;
  }),

  setThreeDS2ContingencyData: action((state, payload) => {
    state.threeDS2ContingencyData = payload;
  }),

  setUserAgreement: action((state, payload) => {
    state.userAgreement = payload;
  }),
});

export const getApiState = <T extends GenericObjectType>(
  overrides = {} as T,
): ApiState & T => ({
  ...getCartState(),
  ...getGriffinState(),
  ...getMiscApiState(),
  ...overrides,
});

export const apiModelFactory = (): ApiModel => ({
  ...getApiState(),
  ...createCartActions(),
  ...createGriffinActions(),
  ...createMiscApiActions(),
});
